import { api, apiService } from '@/services/core/ApiService'
import { whileSameSession } from '@/services/core/sessionGuard'
import { useCartStore } from '@/stores/useCartStore'
import {
    AddCartItemRequestSchema,
    CartSchema,
    CurrentCartResponseSchema,
    type IAddCartItemRequest,
    type ICart,
    type IRemoveCartItemRequest,
    type IUpdateCartItemRequest,
    RemoveCartItemRequestSchema,
    UpdateCartItemRequestSchema,
} from '~/schemas/cart/cart'
import { cartErrorHandler } from './cartErrors'

/**
 * Carrinho da loja (`/cart`). Cada service grava na `useCartStore` o carrinho que a API devolve (padrão do orbit); o
 * 409 `cartNotFound` grava o `cart` da resposta pelo `cartErrorHandler`. O carrinho é da sessão anônima (header
 * GlofiSession) ou do cliente logado (token), decidido pela API.
 */

const storeCart = (cart: ICart | null) => useCartStore.getState().setCart(cart)

// ===== CARRINHO ATUAL =====

/**
 * `GET cart/current`: o carrinho aberto ou `null`, sem criar nada. É a revalidação do carrinho guardado (ao montar,
 * ao voltar o foco da aba, depois de um `cartNotFound`): roda em segundo plano, sem loader nem toast.
 */
export const getCurrentCartService = (): Promise<ICart | null> =>
    apiService(api)
        .get('cart/current', CurrentCartResponseSchema)
        .onSuccess(whileSameSession((response) => storeCart(response.cart)))
        .onError(async () => {}, { useDefaultErrorHandler: false })
        .execute()
        .then((response) => response.cart)

// ===== ITENS =====

/**
 * `POST cart/add-product`. Cria o carrinho se a sessão não tiver um. Erros com a mensagem da API no toast: produto já
 * no carrinho (400), `stockError` (400, com `available`) e `productNotFound` (404).
 */
export async function addCartItemService(request: IAddCartItemRequest): Promise<ICart> {
    return apiService(api)
        .post('cart/add-product', () => AddCartItemRequestSchema.parse(request), CartSchema, {
            meta: { showLoader: true },
        })
        .onSuccess(whileSameSession(storeCart))
        .onError(cartErrorHandler, { useDefaultErrorHandler: false })
        .execute()
}

/** `POST cart/update-product`: troca a quantidade do item. `stockError` traz o estoque atual em `available`. */
export async function updateCartItemService(request: IUpdateCartItemRequest): Promise<ICart> {
    return apiService(api)
        .post('cart/update-product', () => UpdateCartItemRequestSchema.parse(request), CartSchema, {
            meta: { showLoader: true },
        })
        .onSuccess(whileSameSession(storeCart))
        .onError(cartErrorHandler, { useDefaultErrorHandler: false })
        .execute()
}

/** `POST cart/remove-product`: tira o item inteiro (a quantidade só vai para a auditoria). */
export async function removeCartItemService(request: IRemoveCartItemRequest): Promise<ICart> {
    return apiService(api)
        .post('cart/remove-product', () => RemoveCartItemRequestSchema.parse(request), CartSchema, {
            meta: { showLoader: true },
        })
        .onSuccess(whileSameSession(storeCart))
        .onError(cartErrorHandler, { useDefaultErrorHandler: false })
        .execute()
}
